/**
 * Scanner. What the city knows about you, and what it costs to make it forget.
 *
 * **THE FEED IS NOT PUBLISHED YET.** The Scanner as a live band is Phase 9. What IS live, and was
 * being drawn beside a fabricated version of itself, is your Heat: the band, the note, the three
 * ways down and the price the Precinct Captain is asking this minute. All of it comes from
 * `GET /api/heat` and the spend goes through the same `coolOff` action the Home card uses.
 *
 * Everything deleted here was mock data for systems with no storage, and two of the panels were
 * actively misleading rather than merely absent:
 *
 *   - **Cool Off quoted invented prices and dropped Heat LOCALLY.** The meter fell, no cash left
 *     the account, and it snapped back on the next server tick. A player could watch their own
 *     Heat go down and be wrong about it.
 *   - **Risk Status showed live Heat beside a static breakdown summing to 78** and a hardcoded
 *     "Risk of arrest: High", so an account at Heat 0 read 0 percent and then 78 in the same
 *     panel.
 *   - Fifty-one canon names typed into a feed, a wanted list, a jail roster, a seizure log and a
 *     RICO docket, for five systems that have no table anywhere.
 *
 * The forty authored dispatch lines in `content/flavour/scanner.json` ARE read, by
 * `server/src/jobs/beat-materialise.ts`, with `{block}` substituted from the 24 authored landmark
 * names. Publishing them to a client feed is what Phase 9 adds to this screen.
 *
 * **PHASE 9, STAGE A: the warrant ladder is live and it is the fourth thing this header can stop
 * apologising for.** `The Case` renders the real Indictment Progress off `GET /api/heat`, all six
 * rungs whether or not they are reached, and every name and effect line off the wire from
 * `content/warrants.json`. Nothing on it is invented and nothing on it is typed here: the six rung
 * names are in the canon corpus `tools/content/validate.mjs` checks `game/src` against, so writing
 * one as a literal in this file fails the build. That is deliberate, because a six-row ladder that
 * never changes is exactly the thing somebody hardcodes, and CONTENT-BIBLE 10.3 carried a
 * DIFFERENT six rungs, two of them in the opposite order, until Phase 9 corrected it.
 */

import { useEffect, useState } from 'react'
import { A } from '../art'
import type { CooloffMethod } from '@ashport/shared'
import { cents } from '@ashport/shared/formulas'
import { Icon } from '../ui/Icon'
import { Art, Btn, Chip, Col, Meter, Panel, PanelHead, Rail, Row, Stack } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { exact, useGame } from '../game/state'
import { useSession } from '../game/session'

/**
 * The live Heat readout.
 *
 * One number, from the server, with the band and the note the server chose for it. There is no
 * second static breakdown beside it any more: a panel that states a player's Heat twice, and
 * disagrees with itself, is worse than a panel that states it once.
 */
function RiskPanel() {
  const { player } = useGame()
  const heat = useSession((s) => s.heat)
  const band = heat?.band ?? ''
  return (
    <Panel tone="police" style={{ flex: 'none' }}>
      <PanelHead title="Risk Status" icon="scanner" accent="var(--police)" />
      <div style={{ padding: '10px 12px 12px' }}>
        <Row gap={12}>
          <div style={{ textAlign: 'center', flex: 'none' }}>
            <div className="t-label" style={{ fontSize: sz(8.5) }}>Heat</div>
            <div className="t-display" style={{ fontSize: 40, color: 'var(--heat)', lineHeight: 1 }}>
              {player.heat}
            </div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Row gap={7}>
              <span className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.12em' }}>
                {heat?.label ?? ''}
              </span>
              {band !== '' && <Chip c={band === 'rico' ? 'var(--arrest)' : 'var(--police)'}>{band}</Chip>}
            </Row>
            <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
              {heat?.note ?? 'Reading the band.'}
            </p>
            <div style={{ marginTop: 8 }}>
              <Meter v={player.heat} max={100} a="var(--brass-deep)" b="var(--heat)" h={5} />
            </div>
            {heat && heat.floor > 0 && (
              <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 6 }}>
                Your laundering tier holds a floor of {heat.floor}. It does not decay below that.
              </div>
            )}
          </div>
        </Row>
      </div>
    </Panel>
  )
}

/**
 * THE NAME ON THE ROW, AND IT USED TO BE THE ENUM VALUE ITSELF.
 *
 * This panel rendered `m.method.replace(/_/g, ' ')`, which makes a wire identifier into
 * player-facing copy: the row read "go legit" in lower case because that is how the constant is
 * spelled, and adding a method to the union would have written whatever the programmer called it
 * onto the screen. A label is a decision somebody makes on purpose.
 */
const COOLOFF_LABEL: Record<CooloffMethod, string> = {
  bribe: 'Pay the Captain',
  lie_low: 'Lie Low',
  go_legit: 'Run Legit',
  back_to_street: 'Back to the Street',
}

/** The button on each row. Shorter than the title, because the title is already above it. */
const COOLOFF_VERB: Record<CooloffMethod, string> = {
  bribe: 'Pay',
  lie_low: 'Lie low',
  go_legit: 'Go legit',
  back_to_street: 'Back to the street',
}

/**
 * The three ways down and the one way back, priced by the server, spent through the server.
 *
 * Each method carries its own `available` flag and, when it is not, the authored reason why. That
 * is the read that teaches: `go_legit` requires owning a front, and a player who cannot see the
 * locked entry never learns that running a business is a Heat decision. `back_to_street` is on the
 * same list for the same reason: a trade nobody can see the exit from does not read as a trade.
 */
function CoolOffPanel() {
  const heat = useSession((s) => s.heat)
  const busy = useSession((s) => s.busy)
  const coolOff = useSession((s) => s.coolOff)
  const loadHeat = useSession((s) => s.loadHeat)
  const [points, setPoints] = useState('10')

  useEffect(() => {
    void loadHeat().catch(() => undefined)
  }, [loadHeat])

  const available = heat?.maxPoints ?? 0
  const want = Math.max(1, Math.min(Number(points) || 1, Math.max(1, available)))
  const cost = cents((heat?.costPerPoint ?? 0) * want)

  return (
    <Panel tone="danger" style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="Cool Off"
        icon="heat"
        accent="var(--heat)"
        sub={heat ? `${heat.clearsThisEra} cleared this Era` : undefined}
      />
      <div className="scroll-y" style={{ flex: 1, padding: '9px 12px 12px' }}>
        <Col gap={8}>
          {(heat?.methods ?? []).map((m) => (
            <Row
              key={m.method}
              gap={9}
              style={{
                padding: '8px 9px',
                background: 'rgba(255,255,255,.02)',
                border: '1px solid var(--line-soft)',
                alignItems: 'flex-start',
                opacity: m.available ? 1 : 0.62,
              }}
            >
              <Art
                src={A(m.artId as Parameters<typeof A>[0])}
                alt={m.method}
                scrim="none"
                style={{ width: 54, height: 54, flex: 'none', border: '1px solid var(--line)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Row gap={7} style={{ justifyContent: 'space-between' }}>
                  <span className="t-head" style={{ fontSize: 11, color: 'var(--ink-hi)', letterSpacing: '.1em' }}>
                    {COOLOFF_LABEL[m.method] ?? m.method}
                  </span>
                  {!m.available && <Icon n="lock" s={12} c="var(--ink-dim)" />}
                </Row>
                {m.available ? (
                  m.method === 'bribe' ? (
                    available > 0 ? (
                      <>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', marginTop: 4 }}>
                          {exact(cents(heat?.costPerPoint ?? 0))} a point
                          {heat && heat.ricoMultiplier > 1 ? ', at the RICO multiple' : ''}. The price
                          climbs with every clearing of the Era and never resets inside one.
                        </div>
                        <Row gap={6} style={{ marginTop: 7 }}>
                          <input
                            value={points}
                            onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ''))}
                            aria-label="Points of Heat to buy off"
                            style={{
                              width: 62,
                              padding: '5px 8px',
                              background: '#05080a',
                              border: '1px solid var(--line)',
                              color: 'var(--ink)',
                              font: '400 12px/1.2 var(--f-mono, var(--f-body))',
                            }}
                          />
                          <Btn
                            v="danger"
                            size="sm"
                            disabled={busy}
                            onClick={() => void coolOff('bribe', want)}
                          >
                            Pay {exact(cost)}
                          </Btn>
                        </Row>
                      </>
                    ) : (
                      <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', marginTop: 4 }}>
                        There is nothing on you worth paying off.
                      </div>
                    )
                  ) : (
                    <>
                      {/*
                        A LOOKUP RATHER THAN A TWO-WAY TERNARY. The old branch read "anything that
                        is not lie_low is Run Legit", so a fourth method arrived on this panel
                        wearing the third one's description and the third one's button.

                        The percentage is read off the payload rather than typed, because it is a
                        number the server actually applies and a screen quoting its own copy of one
                        is how two statements of a number drift apart.
                      */}
                      <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', marginTop: 4 }}>
                        {m.method === 'lie_low' &&
                          'A safehouse. All income stops, nobody can reach you, minimum thirty minutes. It costs time rather than money, which is the only cost a broke player has.'}
                        {m.method === 'go_legit' &&
                          `Run the front straight for a while. Every job and crime pays ${Math.round((heat?.legitPayoutBp ?? 5500) / 100)} percent while it is on, which is what makes it a choice rather than a free bonus everybody takes on day one.`}
                        {m.method === 'back_to_street' &&
                          'Close the books and go back to the way you were earning. It costs nothing, because you have been paying for it the whole time it was on.'}
                      </div>
                      <Btn
                        v="police"
                        size="sm"
                        style={{ marginTop: 7 }}
                        disabled={busy}
                        onClick={() => void coolOff(m.method, 0)}
                      >
                        {COOLOFF_VERB[m.method] ?? m.method}
                      </Btn>
                    </>
                  )
                ) : (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-dim)', marginTop: 4 }}>{m.lockedReason}</div>
                )}
              </div>
            </Row>
          ))}
        </Col>
        {(heat?.methods.length ?? 0) === 0 && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)' }}>Reading the band.</p>
        )}
      </div>
    </Panel>
  )
}

/**
 * The federal case against you, and the six rungs it climbs through.
 *
 * THIS IS THE COUNTERPLAY AND IT EXISTS BEFORE THE THING IT IS COUNTERPLAY FOR.
 * `indictmentProgress` has been computed and shipped on every snapshot since Phase 2 and no screen
 * has ever read it, so a player's meter could reach 100 with nothing anywhere telling them it was
 * running. The Sealed Indictment takes 40 percent of dirty on hand, a quarter by value of
 * everything unequipped and four hours, and an unpublished meter in front of that is a trap.
 *
 * Every rung ships, reached or not, because a ladder that shows only the rung you are standing on
 * tells you nothing about the one coming. Every name and every effect line is the writer's, off
 * the wire from `content/warrants.json`; none is typed here, and the corpus check in
 * `tools/content/validate.mjs` fails the build if one ever is.
 */
function RicoPanel() {
  const rico = useSession((s) => s.heat?.rico ?? null)
  if (rico === null) {
    return (
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="The Case" icon="scanner" />
        <Col style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 26 }}>
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)' }}>Reading the docket.</p>
        </Col>
      </Panel>
    )
  }

  const open = rico.progress > 0 || rico.gainPerAction > 0
  return (
    <Panel tone={rico.warrant === null ? undefined : 'police'} style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="The Case"
        icon="scanner"
        accent={rico.warrant === null ? undefined : 'var(--police)'}
        sub={`Indictment Progress ${rico.progress} of 100`}
      />
      <div className="scroll-y" style={{ flex: 1, padding: '9px 12px 12px' }}>
        <Meter v={rico.progress} max={100} a="var(--brass-deep)" b="var(--arrest)" h={6} />

        <p style={{ margin: '9px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
          {!open
            ? `Nothing is running. The meter only fills while your Heat is ${rico.ricoAt} or over, and it falls again below ${rico.drainBelow}.`
            : rico.draining
              ? `Falling ${rico.drainPerHour} an hour while your Heat stays under ${rico.drainBelow}. Every point you let go of is a point nobody can hold against you.`
              : `Every action you spend Energy or Nerve on adds ${rico.gainPerAction}. It does not fall until your Heat is under ${rico.drainBelow}.`}
        </p>

        {rico.next !== null && (
          <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 7 }}>
            {rico.next.away} to{' '}
            {/*
              The NAME comes off the ladder the server sent, keyed by the rung id, and is never
              written here. All six are canon in `content/warrants.json` and typing one as a
              literal in this file fails the corpus check in `tools/content/validate.mjs`.
            */}
            {rico.ladder.find((r) => r.kind === rico.next!.kind)?.name ?? ''}
          </div>
        )}

        {/*
          WHAT IT WOULD TAKE, RIGHT NOW, ITEMISED.

          This panel is the reason the Sealed Indictment is allowed to exist. It takes 40 percent
          of dirty on hand, a quarter by value of everything unequipped and four hours, and every
          one of those numbers is here before the meter is anywhere near 100. The figures come
          from the same `seizeGoods` the seizure executes with, so the list a player reads is the
          list it takes.
        */}
        <div
          style={{
            marginTop: 10,
            padding: '8px 9px',
            background: 'rgba(190,60,50,.06)',
            border: '1px solid var(--line-soft)',
          }}
        >
          <div className="t-label" style={{ fontSize: sz(8.5) }}>
            If it fired right now
          </div>
          <Row gap={16} style={{ marginTop: 6, flexWrap: 'wrap' }}>
            <div>
              <div className="t-label" style={{ fontSize: sz(8) }}>Dirty</div>
              <div style={{ fontSize: 13, color: 'var(--arrest)' }}>
                {exact(rico.seizure.dirtyAtRisk)}
              </div>
            </div>
            <div>
              <div className="t-label" style={{ fontSize: sz(8) }}>Goods</div>
              <div style={{ fontSize: 13, color: 'var(--arrest)' }}>
                {exact(rico.seizure.goodsAtRisk)}
              </div>
            </div>
            <div>
              <div className="t-label" style={{ fontSize: sz(8) }}>Cell</div>
              <div style={{ fontSize: 13, color: 'var(--arrest)' }}>
                {Math.round(rico.seizure.jailMs / 3_600_000)}h
              </div>
            </div>
            <div>
              <div className="t-label" style={{ fontSize: sz(8) }}>Legend</div>
              <div style={{ fontSize: 13, color: 'var(--arrest)' }}>{rico.seizure.legendLost}</div>
            </div>
          </Row>
          {rico.seizure.items.length > 0 ? (
            <Col gap={3} style={{ marginTop: 7 }}>
              {rico.seizure.items.map((i) => (
                <Row key={i.itemId} gap={7} style={{ justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-mid)' }}>
                    {i.name}
                    {i.qty > 1 ? ` x${i.qty}` : ''}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-dim)' }}>{exact(i.value)}</span>
                </Row>
              ))}
            </Col>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--ink-dim)', marginTop: 6 }}>
              Nothing in your pockets is worth taking. Equipped gear never is.
            </div>
          )}
        </div>

        <Col gap={6} style={{ marginTop: 10 }}>
          {rico.ladder.map((rung) => (
            <Row
              key={rung.kind}
              gap={9}
              style={{
                padding: '7px 8px',
                background: rung.reached ? 'rgba(190,60,50,.09)' : 'rgba(255,255,255,.02)',
                border: `1px solid ${rung.reached ? 'var(--arrest)' : 'var(--line-soft)'}`,
                alignItems: 'flex-start',
                opacity: rung.reached ? 1 : 0.58,
              }}
            >
              <Art
                src={A(rung.artId as Parameters<typeof A>[0])}
                alt={rung.name}
                scrim="none"
                style={{ width: 30, height: 30, flex: 'none', border: '1px solid var(--line)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Row gap={7} style={{ justifyContent: 'space-between' }}>
                  <span
                    className="t-head"
                    style={{ fontSize: 11, color: 'var(--ink-hi)', letterSpacing: '.1em' }}
                  >
                    {rung.name}
                  </span>
                  <Chip c={rung.reached ? 'var(--arrest)' : 'var(--ink-dim)'}>{rung.ip}</Chip>
                </Row>
                <div style={{ fontSize: 11, color: 'var(--ink-mid)', marginTop: 3, lineHeight: 1.45 }}>
                  {rung.line}
                </div>
              </div>
            </Row>
          ))}
        </Col>
      </div>
    </Panel>
  )
}

/**
 * The band, and every line on it is one a writer wrote.
 *
 * There is no string in this component that reaches a player as dispatch. The forty lines live in
 * `content/flavour/scanner.json`, they are selected by the AUTHORED trigger the server stored
 * against the event, their tokens are filled by `fillScannerLine`, and a line whose tokens cannot
 * all be filled is skipped rather than rendered with its braces showing. 1k.7 ruled that `{time}`
 * is never filled, because there is no timezone anywhere in `Env` and rendering UTC and calling it
 * local would be inventing a fact.
 *
 * The header of this file lists fifty-one canon names that a previous version of this screen typed
 * into a feed, a wanted list, a jail roster, a seizure log and a RICO docket, for five systems with
 * no table anywhere. That is the failure this panel is the correction to, and the corpus check in
 * `tools/content/validate.mjs` is what makes the correction permanent.
 */
function BandPanel() {
  const scan = useSession((s) => s.scanner)
  const loadScanner = useSession((s) => s.loadScanner)

  /*
   * THE MOUNT READ, and its absence is a defect only a browser found.
   *
   * The store's `refresh` list re-reads a screen AFTER an action, which is what keeps a board from
   * showing the state somebody else beat you to. It is not a mount hook, and nothing on the
   * Scanner spends an action against the feed, so the panel rendered its loading state and stayed
   * there forever. `CoolOffPanel` below has carried the same `useEffect` since Phase 8 for exactly
   * this reason, and copying the panel without copying the effect is how the band shipped tuned to
   * nothing while the route beside it answered 200.
   */
  useEffect(() => {
    void loadScanner().catch(() => undefined)
  }, [loadScanner])

  if (scan === null) {
    return (
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="The Band" icon="scanner" />
        <Col style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 26 }}>
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)' }}>Tuning in.</p>
        </Col>
      </Panel>
    )
  }

  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="The Band"
        icon="scanner"
        sub={scan.watched > 0 ? `${scan.watched} on the daily` : 'Quiet citywide'}
      />
      <div className="scroll-y" style={{ flex: 1, minHeight: 0, padding: '9px 12px 12px' }}>
        {/*
          The Beat's district and its own authored alert card. Twelve of these are commissioned,
          one per district, and until now all twelve were covered by the content gate and rendered
          nowhere: `alertId` was validated in the schema and then dropped, because the boot payload
          never projected it. GAME-DESIGN 3.2 asks for exactly this one thing from them.
        */}
        {scan.beat.alertId !== '' && (
          <Row gap={10} style={{ marginBottom: 10, alignItems: 'stretch' }}>
            <Art
              src={A(scan.beat.alertId as Parameters<typeof A>[0])}
              alt={scan.beat.name}
              scrim="none"
              style={{ width: 150, height: 74, flex: 'none', border: '1px solid var(--line)' }}
            />
            <Col gap={3} style={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
              <div className="t-label" style={{ fontSize: sz(8.5) }}>Directed patrol</div>
              <div
                className="t-head"
                style={{ fontSize: 13, color: 'var(--ink-hi)', letterSpacing: '.1em' }}
              >
                {scan.beat.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-mid)' }}>
                Where the money is tonight, and where the cars are.
              </div>
            </Col>
          </Row>
        )}

        {/*
          District pressure: a COUNT, with no handle in it. GAME-DESIGN 3.2's Watched row is
          "3 names in Kepler Row" and nothing more, which is what lets a player at Watched be on
          the Scanner without being on it.
        */}
        {scan.pressure.length > 0 && (
          <Row gap={6} style={{ flexWrap: 'wrap', marginBottom: 10 }}>
            {scan.pressure.map((p) => (
              <Chip key={p.districtId} c={p.band === 'rico' ? 'var(--arrest)' : 'var(--police)'}>
                {p.name} {p.count}
              </Chip>
            ))}
          </Row>
        )}

        {scan.feed.length === 0 ? (
          <Col gap={7} style={{ alignItems: 'center', padding: '26px 18px', textAlign: 'center' }}>
            <Icon n="scanner" s={22} style={{ opacity: 0.4 }} />
            <div
              className="t-head"
              style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '.12em' }}
            >
              Nothing is coming through
            </div>
            <p style={{ margin: 0, maxWidth: 420, fontSize: 11.5, color: 'var(--ink-mid)' }}>
              The city is quiet. It does not stay that way.
            </p>
          </Col>
        ) : (
          <Col gap={0}>
            {scan.feed.map((l) => (
              <Row
                key={l.seq}
                gap={9}
                style={{
                  padding: '6px 2px',
                  borderBottom: '1px solid var(--line-soft)',
                  alignItems: 'flex-start',
                  opacity: l.hot ? 1 : 0.86,
                }}
              >
                <Icon
                  n="scanner"
                  s={12}
                  c={l.channel === 'ch04' ? 'var(--arrest)' : 'var(--police)'}
                  style={{ marginTop: 2, flex: 'none' }}
                />
                <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
                  {l.text}
                </span>
                <span className="t-label" style={{ fontSize: sz(8), flex: 'none' }}>
                  {l.channel}
                </span>
              </Row>
            ))}
          </Col>
        )}
      </div>
    </Panel>
  )
}

/**
 * The wanted list, and what it publishes is a function of the TARGET's own band.
 *
 * GAME-DESIGN 3.2's escalating disclosure table was written as though it were UI chrome and it is
 * the mechanic that makes this different from a Fight List with police flavour. A row here carries
 * a handle and a band from Dangerous, and the exact Heat, the dirty band and the last-seen time
 * only from Critical. The server decides all of that; this renders what arrived and never
 * computes an exposure.
 */
function WantedPanel() {
  const scan = useSession((s) => s.scanner)
  const rows = scan?.wanted ?? []
  return (
    <Panel tone="police" style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="Named"
        icon="scanner"
        accent="var(--police)"
        sub={scan ? `from Heat ${scan.namedFrom}` : undefined}
      />
      <div className="scroll-y" style={{ flex: 1, minHeight: 0, padding: '9px 12px 12px' }}>
        {rows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
            Nobody is named tonight. A handle reaches this list at Heat {scan?.namedFrom ?? 50}, and
            below that a player is a number in a district count and nothing else.
          </p>
        ) : (
          <Col gap={6}>
            {rows.map((r) => (
              <Row
                key={r.playerId}
                gap={8}
                style={{
                  padding: '6px 7px',
                  background: 'rgba(255,255,255,.02)',
                  border: '1px solid var(--line-soft)',
                  alignItems: 'center',
                }}
              >
                <Art
                  src={A(r.portraitId as Parameters<typeof A>[0])}
                  alt={r.handle}
                  scrim="none"
                  style={{ width: 30, height: 30, flex: 'none', border: '1px solid var(--line)' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="t-head"
                    style={{ fontSize: 11, color: 'var(--ink-hi)', letterSpacing: '.08em' }}
                  >
                    {r.handle}
                  </div>
                  <div className="t-label" style={{ fontSize: sz(8) }}>
                    {r.rank}
                  </div>
                </div>
                {/* Zero means NOT PUBLISHED rather than zero Heat: only Critical and above carry
                    a number at all, and the band beside it is what a Dangerous row says. */}
                {r.heat > 0 && (
                  <span style={{ fontSize: 13, color: 'var(--heat)', flex: 'none' }}>{r.heat}</span>
                )}
                {r.dirtyBand > 1 && (
                  <Chip c="var(--gold)">{'$'.repeat(Math.min(3, r.dirtyBand - 1))}</Chip>
                )}
              </Row>
            ))}
          </Col>
        )}
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------------ screen */

export function Scanner() {
  const { stacked } = useViewport()
  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '7px 12px 9px' }}>
            <Icon n="scanner" s={26} c="var(--brass-lit)" />
            <div style={{ flex: 'none' }}>
              <div className="t-display" style={{ fontSize: 21, color: 'var(--ink-hi)' }}>The Scanner</div>
              <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 2 }}>
                What the city knows about you
              </div>
            </div>
          </Row>
        </Panel>
        <RiskPanel />
        <BandPanel />
      </Col>

      <Rail width={360}>
        <WantedPanel />
        <RicoPanel />
        <CoolOffPanel />
      </Rail>
    </Stack>
  )
}
